import React from 'react';
import { Prompt } from 'react-router-dom';
import { ContainsEditor, EditingContext, EditingValue } from '../lib/Editable';
import { LocaleValues, Localized } from '../lib/Locale';
import UserRoleProtected from './UserRoleProtected';
import Editor, { theme } from 'rich-markdown-editor';

import './MultiLanguageContent.scss';
import { Category } from '../data/Category';
import { Clearance } from '../data/sessionSlice';
import { AppConnectedProps } from '../data/store';
import { connect } from 'react-redux';
import { State } from '../data/rootReducer';
import { updatePostContent } from '../data/postsSlice';

const baseTheme = Object.assign({}, theme, {
  fontFamily: 'Nemos',
  background: '#faf4ef',
  link: '#852A11',
  white: '#faf4ef',
  black: '#331e0f',
});

const textTheme = Object.assign({}, baseTheme, {});
const lightTextTheme = Object.assign({}, textTheme, {
  background: '#fffffa',
  white: '#fffffa',
});

const editorTheme = Object.assign({}, baseTheme, {
  background: 'rgba(255, 255, 255, .6)',
});

const connector = connect((state: State) => ({
  posts: state.posts,
}));

interface MultiLanguageContentProps extends AppConnectedProps<typeof connector> {
  className: string;
  category: Category;
  slug: string;
  content: { en?: string; it?: string };
}

class MultiLanguageContent extends React.Component<MultiLanguageContentProps> {
  state = { unsavedChanges: false };
  private editorContent: { en?: string; it?: string } = {};
  private saveHandler: () => Promise<boolean> | null = null;
  private removeSaveHandler: () => void | null = null;

  componentWillUnmount() {
    if (this.removeSaveHandler !== null) {
      this.removeSaveHandler();
    }
  }

  render() {
    return (
      <EditingContext.Consumer>
        {(editable) => {
          const content = this.renderContent(editable);

          return (
            <ContainsEditor>
              {content ? (
                <div className={'editorWrapper ' + this.props.className}>{content}</div>
              ) : (
                ''
              )}
            </ContainsEditor>
          );
        }}
      </EditingContext.Consumer>
    );
  }

  renderContent(editable: EditingValue) {
    if (editable.editing) {
      return (
        <>
          <UserRoleProtected protectedStyle="unlaunched" clearance={Clearance.InDevelopment}>
            {this.renderEditors(editable)}
          </UserRoleProtected>
          <Prompt
            when={this.state.unsavedChanges}
            message="Non hai salvato. Vuoi annullare le modifiche?"
          />
        </>
      );
    }
    if (!!this.props.content.en && !!this.props.content.it) {
      return (
        <Localized
          content={(locale: LocaleValues) => this.renderText(this.chooseLanguage(locale))}
        />
      );
    }
    if (!this.props.content.en && !this.props.content.it) {
      return '';
    }
    return this.renderText(this.chooseLanguage(null));
  }

  renderEditors(editable: EditingValue) {
    if (this.saveHandler == null) {
      this.saveHandler = async () => {
        this.removeSaveHandler();
        await this.save();
        return true;
      };
      this.removeSaveHandler = () => {
        editable.removeSaveHandler(this.saveHandler);
        this.saveHandler = null;
        this.removeSaveHandler = null;
      };
      editable.addSaveHandler(this.saveHandler);
    }
    return (
      <>
        {this.renderEditor('en')}
        {this.renderEditor('it')}
      </>
    );
  }

  renderEditor(language: 'en' | 'it') {
    this.editorContent[language] = this.props.content[language];
    return (
      <>
        <h4>{language === 'en' ? 'English' : 'Italiano'}</h4>
        <Editor
          className="editor"
          autoFocus={true}
          theme={editorTheme}
          // Don't rerender, Editor keeps internal state.
          onChange={(valueFn) => {
            this.editorContent[language] = valueFn();
            this.setState({ unsavedChanges: true });
          }}
          defaultValue={this.props.content[language] ?? ''}
          key={language}
        />
      </>
    );
  }

  renderText(language: 'en' | 'it') {
    return (
      <Editor
        autoFocus={true}
        theme={this.props.category === 'sketches' ? lightTextTheme : textTheme}
        onChange={() => {}}
        defaultValue={this.props.content[language] ?? ''}
        readOnly={true}
        placeholder="..."
        key={language}
      />
    );
  }

  async save() {
    this.props.dispatch(
      updatePostContent(this.props.category, this.props.slug, this.editorContent)
    );
    this.setState({ unsavedChanges: false });
  }

  chooseLanguage(locale?: LocaleValues) {
    const content = this.props.content;
    if (locale && content.en && content.it) {
      return locale.language;
    }
    if (content.it) {
      return 'it';
    }
    return 'en';
  }
}

export default connector(MultiLanguageContent);
